import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { from, of, throwError } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import {
  approveMontshipBooking,
  approveMontshipBookingFailure,
  approveMontshipBookingSuccess,
  batchCreateMontshipBookings,
  batchCreateMontshipBookingsFailure,
  batchCreateMontshipBookingsSuccess,
  createMontshipBooking,
  createMontshipBookingFailure,
  createMontshipBookingSuccess,
  putMontshipBookingOnHold,
  putMontshipBookingOnHoldFailure,
  putMontshipBookingOnHoldSuccess,
  rejectMontshipBooking,
  rejectMontshipBookingFailure,
  rejectMontshipBookingSuccess,
} from './montship-booking.actions'
import { MontshipBookingService } from 'src/services/data/montship-booking.service'
import { sortBy } from 'lodash-es'

@Injectable()
export class MontshipBookingEffects {
  private actions$ = inject(Actions)
  private bookings = inject(MontshipBookingService)
  private toaster = inject(ToasterService)


  createMontshipBooking$ = createEffect(() => this.actions$.pipe(
    ofType(createMontshipBooking),
    switchMap(action =>
      from(this.bookings.sendRequest(action)).pipe(
        map(response => createMontshipBookingSuccess({ booking: response.data })),
        tap(() => this.toaster.success('Montship booking request sent successfully')),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to send Montship booking request', error)
          this.toaster.error('Unable to send Montship booking request', error)
          return of(createMontshipBookingFailure({ error }))
  })))))

  batchCreateMontshipBookings$ = createEffect(() => this.actions$.pipe(
    ofType(batchCreateMontshipBookings),
    switchMap(action =>
      from(this.bookings.createBatchRequest(action?.dealIds)).pipe(
        switchMap(response => 
          response.pipe( // handle the observable response here
            map(response => batchCreateMontshipBookingsSuccess({ bookings: response?.data })),
            tap(() => this.toaster.success('Montship booking requests sent successfully')),
            catchError(error => {
              if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') {
                return throwError(error);
              }
  
              if (error.status === 400) {
                const validatorMessage = error.error?.error?.message;
                const matches = validatorMessage?.match(/requests\.(\d+)\./g);
                const dealIndices = [...new Set(matches?.map(match => parseInt(match?.split('.')[1])))];
                const dealIds = sortBy(action?.dealIds); 
                const failedDealIds = dealIndices?.map(index => dealIds?.[index as number]);
                if (failedDealIds?.length) {
                  this.toaster.warning('No bookings submitted due to missing or incorrect data', `Please review or manually submit the following deals: ${failedDealIds?.join(', ')}`);
                  return of(batchCreateMontshipBookingsFailure({ error }));
                }
              }
  
              console.error('Unable to send Montship booking requests', error);
              this.toaster.error('Unable to send Montship booking requests', error);
              return of(batchCreateMontshipBookingsFailure({ error }));
            })
          )
        )
      )
    )
  ));
  

  approveMontshipBooking$ = createEffect(() => this.actions$.pipe(
    ofType(approveMontshipBooking),
    switchMap(action =>
      from(this.bookings.approve(action.bookingId)).pipe(
        map(response => approveMontshipBookingSuccess({ booking: response })),
        tap(() => this.toaster.success('Montship booking approved successfully')),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to approve Montship booking', error)
          this.toaster.error('Unable to approve Montship booking', error)
          return of(approveMontshipBookingFailure({ error }))
  })))))

  rejectMontshipBooking$ = createEffect(() => this.actions$.pipe(
    ofType(rejectMontshipBooking),
    switchMap(action =>
      from(this.bookings.reject(action.bookingId)).pipe(
        map(response => rejectMontshipBookingSuccess({ booking: response })),
        tap(() => this.toaster.success('Montship booking rejected successfully')),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to reject Montship booking', error)
          this.toaster.error('Unable to reject Montship booking', error)
          return of(rejectMontshipBookingFailure({ error }))
  })))))

  putMontshipBookingOnHold$ = createEffect(() => this.actions$.pipe(
    ofType(putMontshipBookingOnHold),
    switchMap(action =>
      from(this.bookings.putOnHold(action.bookingId)).pipe(
        map(response => putMontshipBookingOnHoldSuccess({ booking: response })),
        tap(() => this.toaster.success('Montship booking put on hold successfully')),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to put Montship booking on hold', error)
          this.toaster.error('Unable to put Montship booking on hold', error)
          return of(putMontshipBookingOnHoldFailure({ error }))
  })))))
}
